import { AppConfig } from '../config';

/**
 * Passthrough camera acquisition, per the passthrough deployment guide:
 *
 * - Probe with `facingMode: { exact: 'environment' }` first: on a provisioned
 *   headset (manual HEADSET_CAMERA grant) this resolves to a passthrough
 *   camera; on anything else it fails fast and tells us why.
 * - Then pin the explicit `deviceId` at the requested resolution - default
 *   constraints only yield 640x480, and the publish path re-pins the track to
 *   the wire size anyway.
 * - EVERY await runs under a timeout. On an unprovisioned browser,
 *   `enumerateDevices()` and `getUserMedia()` HANG rather than reject, which
 *   is indistinguishable from a crash without a deadline.
 *
 * The stream is acquired on the flat page, before Enter - Unity parity, where
 * the camera is Active before the warm-up card enables. No frame leaves the
 * device until the publish gate passes (room + Enter consent).
 */

export type CameraFailureKind =
  /** No mediaDevices API at all (not a secure context, or not a browser). */
  | 'unsupported'
  /** The user (or platform) denied the camera permission. */
  | 'denied'
  /** A camera call hung past its deadline - the unprovisioned-headset signature. */
  | 'timeout'
  /** Cameras exist, but none is environment-facing (Quest 2/Pro, teen account, or no grant). */
  | 'no-back-camera'
  /** Anything else, with the underlying message. */
  | 'error';

export type CameraAcquisition =
  | { readonly ok: true; readonly stream: MediaStream; readonly deviceId: string }
  | { readonly ok: false; readonly kind: CameraFailureKind; readonly message: string };

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new TimeoutError(`TimedOut after ${ms}ms: ${label}`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}

class TimeoutError extends Error {}

function isPermissionDenial(err: unknown): boolean {
  return (
    err instanceof DOMException &&
    (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError' || err.name === 'SecurityError')
  );
}

function failure(kind: CameraFailureKind, message: string): CameraAcquisition {
  return { ok: false, kind, message };
}

/**
 * Acquire the environment-facing (passthrough) camera. Resolves with a live
 * stream or a classified failure - it never hangs and never throws.
 */
export async function acquirePassthroughCamera(
  mediaDevices: MediaDevices | undefined = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined,
): Promise<CameraAcquisition> {
  if (!mediaDevices?.getUserMedia) {
    return failure('unsupported', 'no camera API in this context (needs HTTPS and a real browser)');
  }

  const { width, height, frameRate, acquireTimeoutMs, enumerateTimeoutMs } = AppConfig.camera;

  // ---- probe: ask the PLATFORM for an environment-facing camera, rather
  // than pattern-matching device labels (the guide's verified flow).
  let probe: MediaStream;
  try {
    probe = await withTimeout(
      mediaDevices.getUserMedia({
        video: { facingMode: { exact: 'environment' }, width: { ideal: width }, height: { ideal: height } },
      }),
      acquireTimeoutMs,
      'getUserMedia(environment)',
    );
  } catch (err) {
    if (err instanceof TimeoutError) {
      return failure(
        'timeout',
        'the camera request hung - this browser likely has no headset-camera permission (provision it with the grant script)',
      );
    }
    if (isPermissionDenial(err)) {
      return failure('denied', 'camera permission was denied for this site');
    }
    if (err instanceof DOMException && (err.name === 'OverconstrainedError' || err.name === 'NotFoundError')) {
      // No environment-facing device: unprovisioned Quest 3 (avatar camera
      // only), Quest 2/Pro, or a teen account. Indistinguishable from here -
      // and deliberately handled the same: degrade, do not guess.
      return failure('no-back-camera', 'no passthrough (environment-facing) camera is available on this device');
    }
    return failure('error', err instanceof Error ? err.message : String(err));
  }

  // ---- pin: re-open by explicit deviceId at the working resolution. The
  // probe stream's settings carry the id; a second open with `deviceId` is
  // what reliably yields the full 1280x960 mode.
  const probeTrack = probe.getVideoTracks()[0];
  const deviceId = probeTrack?.getSettings().deviceId ?? '';
  if (!probeTrack || !deviceId) {
    probe.getTracks().forEach((track) => track.stop());
    return failure('error', 'the environment camera resolved without a usable track/deviceId');
  }

  let pinned: MediaStream;
  try {
    pinned = await withTimeout(
      mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: deviceId },
          width: { ideal: width },
          height: { ideal: height },
          frameRate: { ideal: frameRate },
        },
      }),
      enumerateTimeoutMs,
      'getUserMedia(deviceId)',
    );
    probe.getTracks().forEach((track) => track.stop());
  } catch {
    // The pinned re-open failing is not fatal: the probe stream is live and
    // the publish path applies the wire-size constraints to whatever track it
    // is handed. Use the probe stream as-is.
    return { ok: true, stream: probe, deviceId };
  }

  return { ok: true, stream: pinned, deviceId };
}
